import * as React from 'react';

import LocaleContext from 'lib/locale-context';
import { useDetectPrint } from 'lib/use-detect-print';
import { useRouter } from 'next/router';

import { TabIdList } from 'components/tab-list';
import { P1 } from 'components/typography';

type TabListItemProps = {
  id: string;
  label?: string;
  content: React.ReactNode;
  color?: string;
};

type TabListProps = {
  tabs: TabListItemProps[];
  tabId?: TabIdList;
};

export const TabList: React.FC<TabListProps> = ({ tabs, tabId }) => {
  const router = useRouter();
  const lang = React.useContext(LocaleContext);
  const [activeTab, setActiveTab] = React.useState<string>(tabId || tabs[0].id);
  const isPrinting = useDetectPrint();

  const isStatementForStudents = router.asPath.includes(
    '#Processing-we-perform-to-improve-services-for-students',
  );
  React.useEffect(() => {
    if (isStatementForStudents) {
      router
        .replace(
          {
            // change only hash
            //   pathname: window.location.pathname,
            hash: '#information-usage--lawful-basis--Processing-we-perform-to-improve-services-for-students',
            query: window.location.search,
          },
          undefined,
          { shallow: true },
        )
        .catch((error) => {
          if (!error.cancelled) {
            console.log(`error on route replace`, error);
          }
        });
    }
  }, [isStatementForStudents, router]);

  React.useEffect(() => {
    if (router.asPath.includes('#')) {
      if (isPrinting) {
        const pathname = lang ? `/${lang}/print` : '/print';
        router
          .replace(
            {
              pathname,
              hash: '',
              query: window.location.search,
            },
            undefined,
            { shallow: true },
          )
          .catch((error) => {
            if (!error.cancelled) {
              //   throw e;
            }
          });
        return;
      }
      const hash = router.asPath.split('#')[1].split('--')[0];
      if (tabs.some((tab) => tab.id === hash)) {
        setActiveTab(hash);
      }
    }
  }, [router, tabs, isPrinting, lang]);

  const handleTabClick = (id: string) => {
    setActiveTab(id);
    const route = lang ? `/${lang}/#${id}` : `#${id}`;
    router.push(route, undefined, { shallow: true });
  };

  return (
    <div className="tab-list" id="tab-list">
      <div className="tab-list-header" role="tablist" aria-label="Sample Tabs">
        {isPrinting
          ? null
          : tabs.map((tab) =>
              tab.label ? (
                <button
                  key={tab.id}
                  role="tab"
                  id={`${tab.id}`}
                  className={`tab-list-header__item-title${activeTab === tab.id ? ' is-active' : ''}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`tabpanel-${tab.id}`}
                  onClick={() => handleTabClick(tab.id)}
                  style={{
                    backgroundColor: tab.color,
                  }}
                >
                  <P1>{tab.label}</P1>
                </button>
              ) : (
                <div key={tab.id} id={tab.id} />
              ),
            )}
      </div>
      <div className="tab-list-content">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            role="tabpanel"
            id={`tabpanel-${tab.id}`}
            aria-labelledby={`tab-${tab.id}`}
            hidden={activeTab !== tab.id}
            style={{
              backgroundColor: tab.color,
            }}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TabList;
